import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getProjets, getArticles, articleImageUrl, projetImageUrl } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { FiShield, FiUsers, FiDollarSign, FiPhone, FiMail, FiArrowRight, FiArrowLeft, FiMapPin, FiHome, FiCheck, FiMenu, FiX, FiMap, FiTarget, FiTrendingUp, FiSend, FiLock, FiUser, FiLogIn, FiGlobe, FiEye, FiLayers } from 'react-icons/fi';
import logoCohamac from '../assets/logo-COHAMAC.png';
import iconeCohamac from '../assets/icone.png';
import heroBg1 from '../assets/backgrounds/1.jpeg';
import heroBg2 from '../assets/backgrounds/2.jpeg';
import heroBg3 from '../assets/backgrounds/3.jpeg';

const heroSlides = [heroBg1, heroBg2, heroBg3];

const LandingPage = () => {
  const [projets, setProjets] = useState<any[]>([]);
  const [articles, setArticles] = useState<any[]>([]);
  const [mobileNav, setMobileNav] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [contactForm, setContactForm] = useState({ nom: '', email: '', sujet: '', message: '' });
  const [contactSent, setContactSent] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<any>(null);
  const [loginId, setLoginId] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [activeProject, setActiveProject] = useState(0);
  const [heroSlide, setHeroSlide] = useState(0);
  const { isAuthenticated, login, user } = useAuth();
  const navigate = useNavigate();
  const sliderRef = useRef<HTMLDivElement>(null);

  const scrollSlider = (direction: 'left' | 'right') => {
    if (!sliderRef.current) return;
    const scrollAmount = 384;
    sliderRef.current.scrollBy({ left: direction === 'right' ? scrollAmount : -scrollAmount, behavior: 'smooth' });
  };

  useEffect(() => {
    getProjets().then(r => setProjets(r.data)).catch(() => {});
    getArticles().then(r => setArticles(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Scroll reveal animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('revealed'); }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [projets, articles]);

  // Animated counter hook
  const AnimatedCounter = ({ target, suffix = '' }: { target: number; suffix?: string }) => {
    const ref = useRef<HTMLSpanElement>(null);
    const counted = useRef(false);
    useEffect(() => {
      const el = ref.current;
      if (!el) return;
      const observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && !counted.current) {
          counted.current = true;
          const duration = 2000;
          const startTime = performance.now();
          const animate = (now: number) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            el.textContent = Math.floor(eased * target) + suffix;
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      }, { threshold: 0.5 });
      observer.observe(el);
      return () => observer.disconnect();
    }, [target, suffix]);
    return <span ref={ref}>0{suffix}</span>;
  };

  // Auto-rotate hero background slider
  useEffect(() => {
    const timer = setInterval(() => setHeroSlide(s => (s + 1) % heroSlides.length), 6000);
    return () => clearInterval(timer);
  }, []);

  // Auto-rotate projects
  useEffect(() => {
    if (projets.length <= 1) return;
    const timer = setInterval(() => setActiveProject(p => (p + 1) % projets.length), 5000);
    return () => clearInterval(timer);
  }, [projets.length]);

  const handleEspaceMembre = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isAuthenticated) {
      navigate('/admin');
    } else {
      setShowLogin(true);
      setMobileNav(false);
    }
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginId || !loginPassword) { setLoginError('Veuillez remplir tous les champs.'); return; }
    setLoginLoading(true);
    setLoginError('');
    try {
      await login(loginId, loginPassword);
      setShowLogin(false);
      setLoginId(''); setLoginPassword('');
      navigate('/admin');
    } catch (err: any) {
      setLoginError(err.response?.data?.error || 'Erreur de connexion');
    } finally {
      setLoginLoading(false);
    }
  };

  return (
    <div className="lp2">
      {/* ========== NAVBAR (glass) ========== */}
      <header className={`lp2-nav ${scrolled ? 'lp2-nav-scrolled' : ''}`}>
        <div className="lp2-nav-inner">
          <a href="#accueil" className="lp2-logo">
            <img src={logoCohamac} alt="COHAMAC" />
          </a>
          <nav className={`lp2-links ${mobileNav ? 'lp2-links-open' : ''}`}>
            <a href="#accueil" onClick={() => setMobileNav(false)}>Accueil</a>
            <a href="#cooperative" onClick={() => setMobileNav(false)}>A propos</a>
            <a href="#projets" onClick={() => setMobileNav(false)}>Projets</a>
            <a href="#bureau" onClick={() => setMobileNav(false)}>Bureau</a>
            <a href="#contact" onClick={() => setMobileNav(false)}>Contact</a>
          </nav>
          <div className="lp2-nav-right">
            <a href="#" className="lp2-btn-login" onClick={handleEspaceMembre}>
              <FiUser /> <span>Mon espace</span>
            </a>
            <button className="lp2-burger" onClick={() => setMobileNav(!mobileNav)}>
              {mobileNav ? <FiX /> : <FiMenu />}
            </button>
          </div>
        </div>
      </header>

      {/* ========== HERO (full viewport, slider background) ========== */}
      <section className="lp2-hero" id="accueil">
        {/* Background slider */}
        {heroSlides.map((slide, i) => (
          <div
            key={i}
            className={`lp2-hero-slide ${i === heroSlide ? 'lp2-hero-slide-active' : ''}`}
            style={{ backgroundImage: `url(${slide})` }}
          />
        ))}
        <div className="lp2-hero-overlay" />
        {/* Decorative geometric shapes */}
        <div className="lp2-hero-shape lp2-hero-shape-1" />
        <div className="lp2-hero-shape lp2-hero-shape-2" />
        <div className="lp2-hero-content">
          <div className="lp2-hero-badge">Coopérative d'habitat depuis 2017</div>
          <h1>
            Construisons<br/>
            <span className="lp2-hero-accent">votre avenir</span><br/>
            ensemble
          </h1>
          <p className="lp2-hero-sub">
            La COHAMAC accompagne les agents du cadastre vers l'accession
            à la propriété foncière grâce à la coopération et la transparence.
          </p>
          <div className="lp2-hero-btns">
            <a href="#projets" className="lp2-btn-primary">
              Découvrir nos projets <FiArrowRight />
            </a>
            <a href="#" className="lp2-btn-outline" onClick={handleEspaceMembre}>
              <FiLogIn /> Espace membre
            </a>
          </div>
        </div>
        {/* Slider dots */}
        <div className="lp2-hero-dots">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              className={`lp2-hero-dot ${i === heroSlide ? 'active' : ''}`}
              onClick={() => setHeroSlide(i)}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
        {/* Scroll indicator */}
        <div className="lp2-scroll-indicator">
          <div className="lp2-scroll-line" />
        </div>
      </section>

      {/* ========== STATS BAR (floating, glassmorphism) ========== */}
      <section className="lp2-stats-bar reveal">
        <div className="lp2-stats-inner">
          <div className="lp2-stat">
            <span className="lp2-stat-num"><AnimatedCounter target={589} suffix="+" /></span>
            <span className="lp2-stat-label">Parcelles attribuées</span>
          </div>
          <div className="lp2-stat-divider" />
          <div className="lp2-stat">
            <span className="lp2-stat-num"><AnimatedCounter target={19} /></span>
            <span className="lp2-stat-label">Hectares</span>
          </div>
          <div className="lp2-stat-divider" />
          <div className="lp2-stat">
            <span className="lp2-stat-num"><AnimatedCounter target={4} /></span>
            <span className="lp2-stat-label">Sites opérationnels</span>
          </div>
          <div className="lp2-stat-divider" />
          <div className="lp2-stat">
            <span className="lp2-stat-num"><AnimatedCounter target={7} /></span>
            <span className="lp2-stat-label">Années d'existence</span>
          </div>
        </div>
      </section>

      {/* ========== A PROPOS (side by side, modern) ========== */}
      <section className="lp2-about" id="cooperative">
        <div className="lp2-container">
          <div className="lp2-about-grid reveal">
            <div className="lp2-about-visual">
              <div className="lp2-about-img-main">
                <img src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&q=80" alt="Construction" />
              </div>
              <div className="lp2-about-img-float">
                <img src="https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=400&q=80" alt="Quartier" />
              </div>
              <div className="lp2-about-badge">
                <img src={iconeCohamac} alt="COHAMAC" />
              </div>
            </div>
            <div className="lp2-about-text">
              <div className="lp2-section-tag">Qui sommes-nous</div>
              <h2>La coopérative<br/><span className="lp2-text-gold">COHAMAC</span></h2>
              <p>
                Fondée en 2017 par des agents du cadastre, la COHAMAC facilite l'accession
                à la propriété foncière et immobilière de ses membres grâce à la coopération
                organisée, la transparence et l'entraide mutuelle.
              </p>
              <div className="lp2-about-checks">
                <div className="lp2-check"><FiCheck /> Accès facilité à la propriété foncière</div>
                <div className="lp2-check"><FiCheck /> Parcelles viabilisées dans des zones stratégiques</div>
                <div className="lp2-check"><FiCheck /> Accompagnement juridique et technique</div>
                <div className="lp2-check"><FiCheck /> Transparence totale dans la gestion</div>
              </div>
              <a href="#adhesion" className="lp2-btn-primary">
                Comment adhérer <FiArrowRight />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SERVICES / VALEURS (icon cards, dark bg) ========== */}
      <section className="lp2-services reveal">
        <div className="lp2-container">
          <div className="lp2-section-header">
            <div className="lp2-section-tag lp2-tag-light">Nos valeurs</div>
            <h2>Pourquoi nous choisir</h2>
            <p>Des valeurs fortes au service de votre projet immobilier</p>
          </div>
          <div className="lp2-services-grid">
            <div className="lp2-service-card">
              <div className="lp2-service-icon"><FiUsers /></div>
              <h3>Accompagnement Professionnel</h3>
              <p>Un suivi personnalisé pour concrétiser votre projet immobilier. Nos experts vous guident à chaque étape.</p>
            </div>
            <div className="lp2-service-card">
              <div className="lp2-service-icon"><FiHome /></div>
              <h3>Logements Accessibles</h3>
              <p>Des solutions d'habitat à des coûts maîtrisés grâce à la mutualisation des ressources et notre expertise.</p>
            </div>
            <div className="lp2-service-card">
              <div className="lp2-service-icon"><FiEye /></div>
              <h3>Gestion Transparente</h3>
              <p>Un accès permanent aux informations sur l'état d'avancement de vos projets. Transparence et confiance.</p>
            </div>
            <div className="lp2-service-card">
              <div className="lp2-service-icon"><FiShield /></div>
              <h3>Sécurité Foncière</h3>
              <p>Des terrains avec titres fonciers sécurisés, dans des zones à fort potentiel de développement.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== PROJETS (showcase style) ========== */}
      <section className="lp2-projets" id="projets">
        <div className="lp2-container">
          <div className="lp2-section-header reveal">
            <div className="lp2-section-tag">Nos réalisations</div>
            <h2>Nos Projets</h2>
            <p>4 sites opérationnels couvrant 19 hectares au Sénégal</p>
          </div>

          {/* Projects showcase */}
          <div className="lp2-projets-showcase reveal">
            {(projets.length > 0 ? projets : [
              { id: '1', libelle: 'Guédiawaye', localisation: 'PUD de Guédiawaye, Bande des Filaos', superficie: 50000, titre_foncier: 'TF 6104/GW', mode_acquisition: 'Attribution' },
              { id: '2', libelle: 'Niaga / Rufisque', localisation: 'Niaga, Rufisque', superficie: 40000, titre_foncier: 'TF 6093/R', mode_acquisition: 'Achat' },
              { id: '3', libelle: 'Mbour 4 / Thiès', localisation: 'Nouvelle ville de Thiès', superficie: 50000, titre_foncier: 'TF 9810/TH', mode_acquisition: 'Attribution/Achat' },
              { id: '4', libelle: 'Mbodiène / SAPCO', localisation: 'Zone SAPCO, Mbodiène', superficie: 50000, titre_foncier: 'TF 1325/MB', mode_acquisition: 'Attribution' },
            ]).map((p: any, i: number) => (
              <Link
                key={p.id}
                to={projets.length > 0 ? `/projet/${p.id}` : '#'}
                className={`lp2-projet-card ${i === activeProject ? 'lp2-projet-active' : ''}`}
                onMouseEnter={() => setActiveProject(i)}
              >
                <div className="lp2-projet-img">
                  <img
                    src={p.main_image ? projetImageUrl(p.main_image) : `https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=600&q=80`}
                    alt={p.libelle}
                  />
                  <div className="lp2-projet-img-overlay" />
                </div>
                <div className="lp2-projet-info">
                  <div className="lp2-projet-badge">{p.mode_acquisition || 'En cours'}</div>
                  <h3>{p.libelle}</h3>
                  <div className="lp2-projet-loc"><FiMapPin /> {p.localisation || 'Sénégal'}</div>
                  <div className="lp2-projet-meta">
                    <div>
                      <strong>{p.superficie ? (p.superficie >= 10000 ? (p.superficie / 10000) + ' ha' : p.superficie + ' m²') : '-'}</strong>
                      <span>Surface</span>
                    </div>
                    {p.titre_foncier && (
                      <div>
                        <strong>{p.titre_foncier}</strong>
                        <span>Titre Foncier</span>
                      </div>
                    )}
                  </div>
                  <div className="lp2-projet-cta">
                    Voir le projet <FiArrowRight />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Project dots */}
          {projets.length > 1 && (
            <div className="lp2-projet-dots">
              {projets.map((_: any, i: number) => (
                <button
                  key={i}
                  className={`lp2-dot ${i === activeProject ? 'active' : ''}`}
                  onClick={() => setActiveProject(i)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ========== ARTICLES / NEWS ========== */}
      {articles.length > 0 && (
        <section className="lp2-news" id="actualites">
          <div className="lp2-container">
            <div className="lp2-news-top reveal">
              <div>
                <div className="lp2-section-tag">Actualités</div>
                <h2>Dernières nouvelles</h2>
              </div>
              {articles.length > 2 && (
                <div className="lp2-news-arrows">
                  <button onClick={() => scrollSlider('left')}><FiArrowLeft /></button>
                  <button onClick={() => scrollSlider('right')}><FiArrowRight /></button>
                </div>
              )}
            </div>
            <div className="lp2-news-track" ref={sliderRef}>
              {articles.slice(0, 12).map((a) => {
                const defaultImg = 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&q=80';
                const imgSrc = a.imageprincipale ? articleImageUrl(a.imageprincipale) : defaultImg;
                return (
                  <div key={a.id} className="lp2-news-card" onClick={() => setSelectedArticle(a)}>
                    <div className="lp2-news-img">
                      <img src={imgSrc} alt={a.titre} />
                    </div>
                    <div className="lp2-news-body">
                      <div className="lp2-news-date">
                        {(a.created || a.updated) ? new Date(a.created || a.updated).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                      </div>
                      <h3>{a.titre}</h3>
                      <p>{(a.article || '').replace(/<[^>]*>/g, '').substring(0, 100)}...</p>
                      <span className="lp2-news-more">Lire la suite <FiArrowRight /></span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ========== BUREAU / EQUIPE ========== */}
      <section className="lp2-bureau" id="bureau">
        <div className="lp2-container">
          <div className="lp2-section-header reveal">
            <div className="lp2-section-tag lp2-tag-light">Notre équipe</div>
            <h2 className="lp2-text-white">Bureau Exécutif</h2>
            <p className="lp2-text-muted">Les membres du bureau qui dirigent la coopérative</p>
          </div>
          <div className="lp2-bureau-grid reveal">
            {[
              { nom: 'Abdoul Karim KANE', poste: 'Président' },
              { nom: 'Hassane NDOUR', poste: 'Vice-Président' },
              { nom: 'Cheikh CAMARA', poste: 'Secrétaire Général' },
              { nom: 'Victor Babacar FAYE', poste: 'SG Adjoint' },
              { nom: 'Mamadou GAYE', poste: 'Trésorier Général' },
              { nom: 'Abdoulaye DIOP', poste: 'Trésorier Adjoint' },
            ].map((m, i) => (
              <div key={i} className="lp2-bureau-card">
                <div className="lp2-bureau-avatar"><FiUser /></div>
                <h4>{m.nom}</h4>
                <span>{m.poste}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== PERSPECTIVES ========== */}
      <section className="lp2-perspectives reveal">
        <div className="lp2-container">
          <div className="lp2-section-header">
            <div className="lp2-section-tag">Perspectives</div>
            <h2>Nos ambitions</h2>
            <p>La COHAMAC prépare l'avenir avec des projets structurants</p>
          </div>
          <div className="lp2-persp-grid">
            <div className="lp2-persp-card">
              <div className="lp2-persp-num">01</div>
              <div className="lp2-persp-icon"><FiLayers /></div>
              <h3>Centrale d'achat</h3>
              <p>Mise en place d'une centrale d'achat de matériaux de construction pour réduire les coûts pour nos membres.</p>
            </div>
            <div className="lp2-persp-card">
              <div className="lp2-persp-num">02</div>
              <div className="lp2-persp-icon"><FiTarget /></div>
              <h3>Projet de mosquée</h3>
              <p>Construction d'une mosquée avec une commission dédiée pour la gestion et le suivi du projet.</p>
            </div>
            <div className="lp2-persp-card">
              <div className="lp2-persp-num">03</div>
              <div className="lp2-persp-icon"><FiTrendingUp /></div>
              <h3>Développement durable</h3>
              <p>Projets de grands ensembles résidentiels avec des performances environnementales et écologiques.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== CTA BANNER ========== */}
      <section className="lp2-cta">
        <div className="lp2-cta-bg" />
        <div className="lp2-cta-content">
          <h2>Prêt à construire votre avenir ?</h2>
          <p>Rejoignez les 589+ membres qui ont déjà fait confiance à la COHAMAC.</p>
          <div className="lp2-cta-btns">
            <a href="#" className="lp2-btn-primary" onClick={handleEspaceMembre}>
              Accéder à mon espace <FiArrowRight />
            </a>
            <a href="#contact" className="lp2-btn-outline-light">Nous contacter</a>
          </div>
        </div>
      </section>

      {/* ========== ETAPES ADHESION ========== */}
      <section className="lp2-steps" id="adhesion">
        <div className="lp2-container">
          <div className="lp2-section-header reveal">
            <div className="lp2-section-tag">Processus</div>
            <h2>Comment devenir membre ?</h2>
            <p>Un processus simple et transparent en 4 étapes</p>
          </div>
          <div className="lp2-steps-grid reveal">
            {[
              { n: '01', icon: <FiUsers />, t: 'Adhésion', d: "Remplissez le formulaire d'adhésion et réglez votre part sociale pour officialiser votre entrée dans la coopérative." },
              { n: '02', icon: <FiDollarSign />, t: 'Fond de roulement', d: 'Versez votre contribution au fond de roulement qui permet de financer les opérations courantes.' },
              { n: '03', icon: <FiTrendingUp />, t: 'Épargne progressive', d: 'Constituez votre épargne mensuelle selon le plan choisi, à votre rythme.' },
              { n: '04', icon: <FiHome />, t: 'Attribution terrain', d: "Selon votre avancement d'épargne, choisissez votre parcelle parmi les lots disponibles." },
            ].map((s) => (
              <div key={s.n} className="lp2-step-card">
                <div className="lp2-step-num">{s.n}</div>
                <div className="lp2-step-icon">{s.icon}</div>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== CONTACT ========== */}
      <section className="lp2-contact" id="contact">
        <div className="lp2-container">
          <div className="lp2-contact-grid reveal">
            <div className="lp2-contact-info">
              <div className="lp2-section-tag">Contact</div>
              <h2>Nous contacter</h2>
              <p>Notre équipe est à votre disposition pour vous accompagner dans votre projet d'accession à la propriété.</p>
              <div className="lp2-contact-items">
                <div className="lp2-contact-item">
                  <div className="lp2-ci-icon"><FiMapPin /></div>
                  <div>
                    <strong>Adresse</strong>
                    <span>31 Rue de Thiong, Dakar, Sénégal</span>
                  </div>
                </div>
                <div className="lp2-contact-item">
                  <div className="lp2-ci-icon"><FiPhone /></div>
                  <div>
                    <strong>Téléphone</strong>
                    <a href="tel:+221775564215">+221 77 556 42 15</a>
                  </div>
                </div>
                <div className="lp2-contact-item">
                  <div className="lp2-ci-icon"><FiMail /></div>
                  <div>
                    <strong>Email</strong>
                    <a href="mailto:contact@cohamac.com">contact@cohamac.com</a>
                  </div>
                </div>
              </div>
            </div>
            <div className="lp2-contact-form">
              {contactSent ? (
                <div className="lp2-contact-success">
                  <FiCheck style={{ fontSize: '2.5rem', color: '#4CAF50' }} />
                  <h3>Message envoyé !</h3>
                  <p>Nous vous répondrons dans les plus brefs délais.</p>
                  <button className="lp2-btn-primary" onClick={() => { setContactSent(false); setContactForm({ nom: '', email: '', sujet: '', message: '' }); }}>
                    Envoyer un autre message
                  </button>
                </div>
              ) : (
                <form onSubmit={(e) => { e.preventDefault(); setContactSent(true); }}>
                  <h3>Envoyez-nous un message</h3>
                  <div className="lp2-form-row">
                    <div className="lp2-form-group">
                      <label>Nom complet</label>
                      <input type="text" placeholder="Votre nom" value={contactForm.nom} onChange={e => setContactForm({...contactForm, nom: e.target.value})} required />
                    </div>
                    <div className="lp2-form-group">
                      <label>Email</label>
                      <input type="email" placeholder="Votre email" value={contactForm.email} onChange={e => setContactForm({...contactForm, email: e.target.value})} required />
                    </div>
                  </div>
                  <div className="lp2-form-group">
                    <label>Sujet</label>
                    <input type="text" placeholder="Objet de votre message" value={contactForm.sujet} onChange={e => setContactForm({...contactForm, sujet: e.target.value})} required />
                  </div>
                  <div className="lp2-form-group">
                    <label>Message</label>
                    <textarea placeholder="Décrivez votre demande..." rows={5} value={contactForm.message} onChange={e => setContactForm({...contactForm, message: e.target.value})} required />
                  </div>
                  <button type="submit" className="lp2-btn-submit"><FiSend /> Envoyer le message</button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="lp2-footer">
        <div className="lp2-container">
          <div className="lp2-footer-grid">
            <div className="lp2-footer-brand">
              <img src={logoCohamac} alt="COHAMAC" className="lp2-footer-logo" />
              <p>La coopérative a pour but de contribuer au développement de l'immobilier et de l'habitat social au profit de ses membres.</p>
            </div>
            <div className="lp2-footer-col">
              <h4>Liens rapides</h4>
              <a href="#accueil">Accueil</a>
              <a href="#cooperative">A propos</a>
              <a href="#projets">Nos projets</a>
              <a href="#bureau">Bureau</a>
              <a href="#adhesion">Adhésion</a>
            </div>
            <div className="lp2-footer-col">
              <h4>Contact</h4>
              <span><FiPhone /> +221 77 556 42 15</span>
              <span><FiMapPin /> 31 Rue de Thiong, Dakar</span>
              <span><FiMail /> contact@cohamac.com</span>
            </div>
          </div>
          <div className="lp2-footer-bottom">
            <div className="lp2-footer-bottom-left">
              <img src={iconeCohamac} alt="" className="lp2-footer-icone" />
              <span>COHAMAC</span>
            </div>
            <span>Copyright COHAMAC {new Date().getFullYear()}. Tous droits réservés</span>
          </div>
        </div>
      </footer>

      {/* ========== ARTICLE POPUP ========== */}
      {selectedArticle && (
        <div className="article-modal-overlay" onClick={() => setSelectedArticle(null)}>
          <div className="article-modal" onClick={e => e.stopPropagation()}>
            <button className="article-modal-close" onClick={() => setSelectedArticle(null)}><FiX /></button>
            {selectedArticle.imageprincipale && (
              <div className="article-modal-image">
                <img src={articleImageUrl(selectedArticle.imageprincipale)} alt={selectedArticle.titre} />
              </div>
            )}
            <div className="article-modal-header">
              <div className="article-modal-date">
                {(selectedArticle.created || selectedArticle.updated) ? new Date(selectedArticle.created || selectedArticle.updated).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
              </div>
              <h2>{selectedArticle.titre}</h2>
              {selectedArticle.projet_libelle && (
                <span className="article-modal-badge">{selectedArticle.projet_libelle}</span>
              )}
            </div>
            <div className="article-modal-body" dangerouslySetInnerHTML={{ __html: selectedArticle.article || '' }} />
          </div>
        </div>
      )}

      {/* ========== LOGIN POPUP ========== */}
      {showLogin && (
        <div className="login-modal-overlay" onClick={() => setShowLogin(false)}>
          <div className="login-modal" onClick={e => e.stopPropagation()}>
            <button className="login-modal-close" onClick={() => setShowLogin(false)}><FiX /></button>
            <div className="login-modal-logo">
              <img src={logoCohamac} alt="COHAMAC" style={{ height: 60 }} />
            </div>
            <h2>Connexion</h2>
            <p className="login-modal-sub">Accédez à votre espace membre</p>
            {loginError && <div className="alert alert-error">{loginError}</div>}
            <form onSubmit={handleLoginSubmit}>
              <div className="form-group">
                <label><FiUser /> Matricule / Email / Téléphone</label>
                <input type="text" className="form-input" placeholder="Entrez votre identifiant" value={loginId} onChange={e => setLoginId(e.target.value)} required />
              </div>
              <div className="form-group">
                <label><FiLock /> Mot de passe</label>
                <input type="password" className="form-input" placeholder="Entrez votre mot de passe" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} required />
              </div>
              <button type="submit" className="btn-login" disabled={loginLoading}>
                <FiLogIn /> {loginLoading ? 'Connexion...' : 'Se connecter'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
